import React, { useEffect, useState } from "react";
import LayoutBuyerSeller from "../../components/layout/LayoutBuyerSeller";
import VerifiedIcon from "../../assets/icon/verified-icons.png";
import VerifiedIconGray from "../../assets/icon/verified-Icon-gray.png";
import { FiArrowLeft } from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import apiService from "../../actions/apiService";
import Toast from "../../components/Toast";
import ConfirmTransactionModal from "../../components/models/ConfirmTransactionModal";
import { useAuth } from "../../context/authContext";

const STATUS_PILL = {
  pending: "bg-[#FFC49F] text-[#D35100]",
  accepted: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  buyer_confirmed: "bg-[#FFC49F] text-[#D35100]",
  confirmed: "bg-[#B9FFCC] text-[#6C8F75]",
  released: "bg-[#B9FFCC] text-[#6C8F75]",
  disputed: "bg-red-100 text-red-700",
  resolved: "bg-blue-100 text-blue-700",
  cancelled: "bg-[#FF5040] text-[#ffffff]",
  payment_received: "bg-[#E0E0E0] text-[#575757]",
};

// Helper function to get status display text and pill key for buyers
const getStatusDisplay = (
  status,
  adminVerificationStatus,
  paymentCancelledReason,
  paymentInitiatedAt
) => {
  if (status === "cancelled") {
    if (paymentCancelledReason === "item_not_authentication") {
      return {
        text: "Cancelled",
        pillKey: "cancelled",
      };
    }
    return { text: "Cancelled", pillKey: "cancelled" };
  }
  // If payment was initiated (buyer clicked "I made the transfer"), show "Verification in progress"
  if (status === "pending" && paymentInitiatedAt) {
    return {
      text: "Verification in progress",
      smallText: "",
      pillKey: "payment_received",
    };
  }
  if (status === "pending") {
    return {
      text: "pending payment",
      smallText: "",
      pillKey: "pending",
    };
  }
  // After admin accepts payment (status="accepted" and admin_verification_status="verified"), show "Funds on hold"
  if (status === "accepted" && adminVerificationStatus === "verified") {
    return {
      text: "funds on hold",
      smallText: "",
      pillKey: "buyer_confirmed",
    };
  }
  if (status === "buyer_confirmed") {
    return {
      text: "funds on hold",
      smallText: "",
      pillKey: "buyer_confirmed",
    };
  }
  if (status === "confirmed") {
    return {
      text: "completed",
      pillKey: "confirmed",
      smallText: "",
    };
  }
  if (status === "released") {
    return { text: "completed", pillKey: "released" };
  }
  if (
    adminVerificationStatus === "released" ||
    adminVerificationStatus === "Released"
  ) {
    return { text: "completed", pillKey: "released" };
  }
  return {
    text: status.charAt(0).toUpperCase() + status.slice(1).replace("_", " "),
    pillKey: status,
  };
};

// Thumbnail component kept previously is not necessary for static preview grid

const ListingDetails = () => {
  const { id } = useParams();
  const itemId = id || "";

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [sendingOffer, setSendingOffer] = useState(false);
  const [offerError, setOfferError] = useState("");
  const [isOfferOpen, setIsOfferOpen] = useState(false);
  const [offerPrice, setOfferPrice] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [offerHistory, setOfferHistory] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState(null);
  const [includeDeliveryFee, setIncludeDeliveryFee] = useState(false);
  const [deliveryFeeError, setDeliveryFeeError] = useState("");
  const [sellerStats, setSellerStats] = useState({
    totalSales: 0,
    totalSoldValue: 0,
    sellerRating: 0,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [modalError, setModalError] = useState(null);

  const navigate = useNavigate();
  const { user } = useAuth();

  const getUiStatus = (item) => {
    // First check if there's a completed transaction
    if (
      item?.transactions &&
      Array.isArray(item.transactions) &&
      item.transactions.length > 0
    ) {
      const completedTransaction = item.transactions.find(
        (t) =>
          t.status?.toLowerCase() === "released" ||
          t.status?.toLowerCase() === "completed" ||
          t.payment_status?.toLowerCase() === "completed"
      );
      if (completedTransaction) {
        return "completed";
      }
    }

    const status = (item?.status || "").toLowerCase();
    if (status === "cancelled") return "cancelled";
    if (status === "released" || status === "completed" || status === "sold")
      return "completed";
    if (status === "payment_in_escrow") return "on_hold";
    if (status === "admin_checking_payment") return "verifying";
    if (status === "pending") return "pending";
    return "available";
  };

  const renderStatusPill = (item, offerHistory = [], currentUserId = null) => {
    // If current user has a pending offer, show "Offer sent"
    const pendingOffer = offerHistory.find(
      (req) => req.status === "pending" && req.buyer_id === currentUserId
    );
    if (pendingOffer) {
      return (
        <div className="justify-start items-center flex gap-1 font-medium w-fit px-2.5 py-1 rounded-full bg-[#5C31AC] text-[#ffffff]">
          Offer sent
        </div>
      );
    }

    // Check if item has a pending invitation (offer received) - only show for pending, not rejected
    if (item?.pendingInvitation?.status === "pending") {
      return (
        <div className="justify-start items-center flex gap-1 font-medium w-fit px-2.5 py-1 rounded-full bg-[#8794E0] text-[#FFFFFF]">
          Offer received
        </div>
      );
    }

    // Use the same status determination logic as Transactions.js and TransDetails.js
    // Get the latest transaction from the transactions array
    const transactions = item?.transactions || [];
    const latestTransaction = transactions.length > 0 ? transactions[0] : null;

    // Determine status using the same logic as mapApiTransactionToRow in Transactions.js
    let status =
      latestTransaction?.status ||
      latestTransaction?.payment_status ||
      latestTransaction?.transfer_status ||
      latestTransaction?.admin_verification_status ||
      item?.status;
    status = (status ? String(status) : "pending").toLowerCase();

    // Get adminVerificationStatus and paymentCancelledReason from transaction
    const adminVerificationStatus = latestTransaction?.admin_verification_status
      ? String(latestTransaction.admin_verification_status).toLowerCase()
      : "";
    const paymentCancelledReason =
      latestTransaction?.payment_cancelled_reason || null;

    // Get payment_initiated_at from transaction
    const paymentInitiatedAt = latestTransaction?.payment_initiated_at || null;

    // Use getStatusDisplay to format the status
    const statusDisplay = getStatusDisplay(
      status,
      adminVerificationStatus,
      paymentCancelledReason,
      paymentInitiatedAt
    );

    return (
      <div className="flex flex-col gap-1">
        <span
          className={`justify-start items-center flex gap-1 font-medium w-fit px-2.5 py-1 rounded-full capitalize ${
            STATUS_PILL[statusDisplay.pillKey] ||
            STATUS_PILL[status] ||
            "bg-gray-100 text-gray-700"
          }`}
        >
          {statusDisplay.text}
        </span>
        {statusDisplay.smallText && (
          <span className="text-[12px] text-[#1E1E1E] opacity-50">
            {statusDisplay.smallText}
          </span>
        )}
      </div>
    );
  };

  const handleSubmitOffer = async (e) => {
    e?.preventDefault?.();
    if (!item?.item) return;
    const amount = Number(offerPrice);
    if (!amount || amount <= 0 || Number.isNaN(amount)) {
      setOfferError("Please enter a valid offer price");
      return;
    }
    // Check if delivery fee is needed but not available
    if (item?.item?.delivered_to_door && includeDeliveryFee && !deliveryFee) {
      setOfferError(
        "Delivery fee information is not available. Please try again."
      );
      return;
    }
    // Calculate total amount (offer price + service fee)
    // const serviceFee = amount * (item?.pricing?.buyerFeePercentage / 100);
    const totalAmount = amount;

    setOfferError("");
    setSendingOffer(true);
    const payload = {
      item_id: item?.item?.id || itemId,
      recipient_user_id: item?.item?.seller?.id,
      offer_amount: totalAmount, // Send total amount (offer + service fee)
      message: "",
      created_by: "buyer",
      is_delivered_door:
        includeDeliveryFee && Boolean(item?.item?.delivered_to_door),
    };
    const res = await apiService.post("/requests", payload);
    if (!res.success) {
      setOfferError(res.error || "Failed to send offer");
    } else {
      setIsOfferOpen(false);
      setOfferPrice("");
      setIncludeDeliveryFee(false);
      // Show success toast
      setToastMessage("Offer has been sent successfully");
      // Refresh offer history so UI updates without full reload
      try {
        const historyResult = await apiService.get(
          `/requests/item/${item?.item?.id || itemId}/history`
        );
        if (historyResult.success && historyResult.data?.requests) {
          setOfferHistory(historyResult.data.requests);
        }
      } catch (_) {
        /* history refresh best-effort */
      }
    }
    setSendingOffer(false);
  };

  const handleContactSeller = () => {
    if (!item?.item?.seller?.email) return;
    const mailtoLink = `mailto:${item?.item?.seller?.email}`;
    window.open(mailtoLink, "_self");
  };

  // Function to open confirmation modal
  const handleOpenConfirmModal = () => {
    setModalError(null);
    setModalOpen(true);
  };

  // Function to confirm transaction
  const handleConfirmTransaction = async () => {
    const transactions = item?.item?.transactions || [];
    const latestTransaction = transactions.length > 0 ? transactions[0] : null;

    if (!latestTransaction?.id) return;

    setConfirmingId(latestTransaction.id);
    try {
      const response = await apiService.post(
        `/transactions/${latestTransaction.id}/confirm-buyer`
      );
      if (response?.success) {
        setToastMessage("Transaction confirmed successfully!");
        setModalOpen(false);
        // Reload the item data
        const result = await apiService.get(`/items/buyer/items/${itemId}`);
        if (result.success) {
          setItem(result.data);
        }
      } else {
        setModalError(response?.error || "Failed to confirm transaction");
      }
    } catch (err) {
      setModalError("Network error. Please try again.");
    } finally {
      setConfirmingId(null);
    }
  };

  // Function to close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setModalError(null);
  };

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError("");
      // Try normal item details first
      const result = await apiService.get(`/items/buyer/items/${itemId}`);
      if (!active) return;
      if (result.success) {
        setItem(result.data);
        // Fetch delivery fee if authentication/delivery to door applies
        const hasAcceptedOfferWithAuth = Boolean(
          result.data?.acceptedOffer?.is_delivered_door
        );
        const itemSupportsDelivery = result.data?.item?.delivered_to_door;
        if (hasAcceptedOfferWithAuth || itemSupportsDelivery) {
          try {
            const configResult = await apiService.get(
              "/transactions/delivery-fee"
            );
            if (configResult.success && configResult.data?.delivery_fee) {
              setDeliveryFee(Number(configResult.data.delivery_fee));
            }
          } catch (configError) {
            console.error("Failed to fetch delivery fee:", configError);
          }
        }
        // Fetch seller stats if seller id present
        const sellerId = result?.data?.item?.seller?.id;
        if (sellerId) {
          try {
            const statsRes = await apiService.get(
              "/transactions/seller-stats",
              {
                seller_id: sellerId,
              }
            );
            if (statsRes?.success && statsRes.data) {
              setSellerStats({
                totalSales: statsRes.data.totalSales || 0,
                totalSoldValue: statsRes.data.totalSoldValue || 0,
                sellerRating:
                  typeof statsRes.data.sellerRating === "number"
                    ? statsRes.data.sellerRating
                    : 0,
              });
            }
          } catch (_) {
            /* ignore stats errors */
          }
        }
      } else {
        setError(result.error || "Failed to load item");
      }
      setLoading(false);
    }
    load();
    return () => {
      active = false;
    };
  }, [itemId]);

  // Fetch offer history for this item - only for current buyer
  useEffect(() => {
    let active = true;
    async function loadHistory() {
      if (!item?.item?.id || !user?.id) return;
      try {
        const result = await apiService.get(
          `/requests/item/${item.item.id}/history`
        );
        if (!active) return;
        if (result.success && result.data?.requests) {
          setOfferHistory(result.data.requests);
        }
      } catch (err) {
        console.error("Failed to load offer history:", err);
      }
    }
    loadHistory();
    return () => {
      active = false;
    };
  }, [item?.item?.id, user?.id]);

  const imageBaseUrl = process.env.REACT_APP_IMAGE_URL || "";
  const imageUrls = (item?.item?.images || []).map(
    (img) => `${imageBaseUrl}${img.file_url}`
  );

  const mainImageSrc = imageUrls[selectedImageIdx] || "";

  const renderPrimaryAction = () => {
    if (!item?.item) return null;

    // Show "See offer" button if there's a pending invitation (not rejected)
    if (item?.item?.pendingInvitation?.status === "pending") {
      return (
        <Link
          to={`/buyer/requests`}
          className="mt-8 flex-1 md:flex-none bg-blue-50 text-esblue w-full text-[14px] py-2.5 rounded-[8px] inline-flex items-center justify-center gap-4"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M10 9C11.6569 9 13 7.65685 13 6C13 4.34315 11.6569 3 10 3C8.34315 3 7 4.34315 7 6C7 7.65685 8.34315 9 10 9Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 17C3 13.6863 5.68629 11 9 11H11C14.3137 11 17 13.6863 17 17"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>See offer</span>
        </Link>
      );
    }

    // Use the same status determination logic as Transactions.js and TransDetails.js
    // Get the latest transaction from the transactions array
    const transactions = item?.item?.transactions || [];
    const latestTransaction = transactions.length > 0 ? transactions[0] : null;

    // Determine status using the same logic as mapApiTransactionToRow in Transactions.js
    let status =
      latestTransaction?.status ||
      latestTransaction?.payment_status ||
      latestTransaction?.transfer_status ||
      latestTransaction?.admin_verification_status ||
      item?.item?.status;
    status = (status ? String(status) : "pending").toLowerCase();

    // Get adminVerificationStatus and paymentCancelledReason from transaction
    const adminVerificationStatus = latestTransaction?.admin_verification_status
      ? String(latestTransaction.admin_verification_status).toLowerCase()
      : "";
    const paymentCancelledReason =
      latestTransaction?.payment_cancelled_reason || null;

    // Get payment_initiated_at from transaction
    const paymentInitiatedAt = latestTransaction?.payment_initiated_at || null;

    // Get buyer and seller confirmation status
    const buyerConfirmed = Boolean(latestTransaction?.buyer_confirmed);
    const sellerConfirmed = Boolean(latestTransaction?.seller_confirmed);

    // Use getStatusDisplay to format the status
    const statusDisplay = getStatusDisplay(
      status,
      adminVerificationStatus,
      paymentCancelledReason,
      paymentInitiatedAt
    );

    // Don't show any action if item is cancelled
    if (status === "cancelled") {
      return null;
    }

    // Show Confirm button for accepted/confirmed/buyer_confirmed status when buyer hasn't confirmed
    // Don't show for released or cancelled status
    const showConfirmButton =
      !buyerConfirmed &&
      status !== "cancelled" &&
      status !== "released" &&
      (["accepted", "confirmed", "buyer_confirmed"].includes(status) ||
        sellerConfirmed);

    if (showConfirmButton) {
      return (
        <button
          onClick={handleOpenConfirmModal}
          className="mt-8 flex-1 md:flex-none bg-[#B9FFCC] text-[#16653F] w-full text-[14px] py-2.5 rounded-[8px] inline-flex items-center justify-center gap-4 font-[600]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="13"
            viewBox="0 0 18 13"
            fill="none"
          >
            <path
              d="M17 1L6 12L1 7"
              stroke="#16653F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Confirm transaction done</span>
        </button>
      );
    }

    // Show "Proceed to payment" for pending status (when payment not yet initiated)
    if (status === "pending" && !paymentInitiatedAt) {
      return (
        <button
          onClick={() =>
            navigate(`/buyer/payment-initiate/${item?.item?.id || itemId}`)
          }
          className="mt-8 flex-1 md:flex-none bg-[#B9FFCC] text-[#16653F] w-full text-[14px] py-2.5 rounded-[8px] inline-flex items-center justify-center gap-4"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
          >
            <path
              d="M20.5 6L9.5 17L4.5 12"
              stroke="#16653F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Proceed to payment</span>
        </button>
      );
    }

    // Show status button based on statusDisplay
    if (statusDisplay.pillKey === "payment_received") {
      return (
        <button
          disabled
          className="mt-8 flex-1 md:flex-none bg-[#E0E0E0] text-[#575757] w-full text-[14px] py-2.5 rounded-[8px] inline-flex items-center justify-center gap-4 cursor-not-allowed"
        >
          <span>{statusDisplay.text}</span>
        </button>
      );
    }

    if (statusDisplay.pillKey === "buyer_confirmed") {
      // If buyer already confirmed, show disabled "Funds on hold" button
      if (buyerConfirmed) {
        return (
          <button
            disabled
            className="mt-8 flex-1 md:flex-none bg-[#FFC49F] text-[#D35100] w-full text-[14px] py-2.5 rounded-[8px] inline-flex items-center justify-center gap-4 cursor-not-allowed"
          >
            <span>{statusDisplay.text}</span>
          </button>
        );
      }
      // This case is already handled above with showConfirmButton
    }

    if (
      statusDisplay.pillKey === "confirmed" ||
      statusDisplay.pillKey === "released"
    ) {
      return (
        <button
          disabled
          className="mt-8 flex-1 md:flex-none bg-[#B9FFCC] text-[#16653F] w-full text-[14px] py-2.5 rounded-[8px] inline-flex items-center justify-center gap-4 cursor-not-allowed"
        >
          <span>{statusDisplay.text}</span>
        </button>
      );
    }

    if (statusDisplay.pillKey === "pending") {
      return (
        <button
          onClick={() =>
            navigate(`/buyer/payment-initiate/${item?.item?.id || itemId}`)
          }
          className="mt-8 flex-1 md:flex-none bg-[#B9FFCC] text-[#16653F] w-full text-[14px] py-2.5 rounded-[8px] inline-flex items-center justify-center gap-4"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
          >
            <path
              d="M20.5 6L9.5 17L4.5 12"
              stroke="#16653F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Proceed to payment</span>
        </button>
      );
    }

    // Default: Show "Send an offer" button
    return (
      <button
        onClick={() => {
          setOfferError("");
          setOfferPrice("");
          setIncludeDeliveryFee(false);
          setDeliveryFeeError("");
          setIsOfferOpen(true);
        }}
        className="mt-8 flex-1 md:flex-none bg-blue-gradient text-white w-full text-[14px] py-2.5 rounded-[8px] inline-flex items-center justify-center gap-4"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M6 16C6 18.2091 7.79086 20 10 20H14C16.2091 20 18 18.2091 18 16C18 13.7909 16.2091 12 14 12H10C7.79086 12 6 10.2091 6 8C6 5.79086 7.79086 4 10 4H14C16.2091 4 18 5.79086 18 8M12 2V22"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Send an offer</span>
      </button>
    );
  };

  if (loading) {
    return (
      <LayoutBuyerSeller>
        <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
        <div className="relative z-[10] px-5 lg:px-0">
          <div className="flex items-center justify-center py-24">
            <span className="text-[14px] font-[600] text-white lg:text-esblue">
              Loading details...
            </span>
          </div>
        </div>
      </LayoutBuyerSeller>
    );
  }

  return (
    <LayoutBuyerSeller>
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative z-[10] px-5 lg:px-0">
        {error && (
          <div className="mb-8 text-[12px] md:text-[14px] opacity-70 text-white lg:text-[#1E1E1E]">
            {error}
          </div>
        )}
        <div className="text-[14px] flex items-center justify-start text-white lg:text-esblue font-[600] mb-8">
          <Link
            className="flex items-center justify-center gap-1"
            to={"/buyer/requests"}
          >
            <FiArrowLeft className="w-3.5 h-3.5" /> Back to search results
          </Link>
        </div>
        <h1 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
          {item?.item?.title || "N/A"}
        </h1>
        <p className="text-[12px] md:text-[14px] opacity-70 text-white lg:text-[#1E1E1E] mt-1">
          Main information and short description of the listing, keywords, year
        </p>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-5 items-start">
          {/* Left column */}
          <div className="space-y-5">
            {/* Gallery */}
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <div className="mb-4 h-[292px] w-full bg-gray-100 rounded-[12px] overflow-hidden relative">
                {mainImageSrc ? (
                  <img
                    src={mainImageSrc}
                    alt="listing"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100" />
                )}
                <div className="absolute left-3 top-3">
                  {renderStatusPill(item?.item, offerHistory, user?.id)}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                {imageUrls.map((url, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setSelectedImageIdx(idx)}
                    className={`h-[104px] cursor-pointer w-full rounded-[12px] overflow-hidden bg-white border ${
                      idx === selectedImageIdx ? "" : ""
                    }`}
                  >
                    <img
                      src={url}
                      alt={`thumb-${idx}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
                {Array.from({ length: Math.max(0, 3 - imageUrls.length) }).map(
                  (_, i) => (
                    <div
                      key={`ph-${i}`}
                      className="h-[104px] w-full rounded-[12px] overflow-hidden bg-gray-100"
                    ></div>
                  )
                )}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik">
                Description
              </h3>
              <p className="text-[#000025] font-[400] text-[16px] opacity-50 mt-4">
                {item?.item?.description ? item?.item?.description : "-"}
              </p>
            </div>

            {/* Specifications */}
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik">
                Specifications
              </h3>
              <div className="grid mt-4 grid-cols-1 gap-x-10 gap-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[16px] font-[400] text-[#000025] opacity-50">
                    Plate number
                  </span>
                  <span className="text-[16px] font-[600] text-[#000025] opacity-50">
                    {item?.item?.plate_number
                      ? item?.item?.plate_number
                      : "N/A"}
                  </span>
                </div>
                {Object.entries(item?.item?.custom_properties || {}).map(
                  ([k, v], i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-[16px] font-[400] text-[#000025] opacity-50">
                        {k}
                      </span>
                      <span className="text-[16px] font-[600] text-[#000025] opacity-50">
                        {v}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-5">
            {/* Listing details */}
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik">
                Listing details
              </h3>
              <p className="text-[#000025] font-[400] text-[16px] opacity-50 mt-4">
                Secure transaction through escrow
              </p>
              <div className="mt-8">
                <div className="flex items-center justify-between text-[14px]">
                  <span className="text-[16px] text-[#1E1E1E] font-[600]">
                    Item price
                  </span>
                  <span className="text-[18px] text-[#000025] font-[700]">
                    {item?.pricing?.basePrice} €
                  </span>
                </div>
                <div className="flex items-center mt-2 justify-between text-[14px]">
                  <span className="text-[16px] opacity-50 text-[#1E1E1E] font-[400]">
                    Service fee ({item?.pricing?.buyerFeePercentage}%)
                  </span>
                  <span className="text-[16px] opacity-50 text-[#000025] font-[400]">
                    {item?.pricing?.platformFee} €
                  </span>
                </div>
                <div className="flex items-center mt-8 justify-between">
                  <span className="text-[16px] text-[#1E1E1E] font-[600]">
                    Total
                  </span>
                  <span className="text-big-price md:text-big-price-d font-[700] text-esblue">
                    {(() => {
                      const acceptedOffer = item?.acceptedOffer;
                      const transactions = item?.item?.transactions || [];
                      const latestTransaction =
                        transactions.length > 0 ? transactions[0] : null;
                      const hasAuthentication =
                        acceptedOffer?.is_delivered_door === true ||
                        latestTransaction?.is_delivered_door === true ||
                        item?.transaction?.is_delivered_door === true;
                      const authenticationFee =
                        hasAuthentication && deliveryFee
                          ? Number(deliveryFee)
                          : 0;
                      const baseTotal = Number(item?.pricing?.finalPrice) || 0;
                      return (baseTotal + authenticationFee).toFixed(2);
                    })()}{" "}
                    €
                  </span>
                </div>
                {offerError && (
                  <div className="mt-8 text-[12px] md:text-[14px] opacity-70 text-white lg:text-[#1E1E1E]">
                    {offerError}
                  </div>
                )}
                {(() => {
                  return (
                    item?.item &&
                    getUiStatus(item.item) !== "cancelled" &&
                    renderPrimaryAction()
                  );
                })()}
                {/* See receipt button for completed transactions */}
                {(() => {
                  const transactions = item?.item?.transactions || [];
                  const latestTransaction =
                    transactions.length > 0 ? transactions[0] : null;

                  if (!latestTransaction) return null;

                  let status =
                    latestTransaction?.status ||
                    latestTransaction?.payment_status ||
                    latestTransaction?.transfer_status ||
                    latestTransaction?.admin_verification_status ||
                    item?.item?.status;
                  status = (status ? String(status) : "pending").toLowerCase();

                  const adminVerificationStatus =
                    latestTransaction?.admin_verification_status
                      ? String(
                          latestTransaction.admin_verification_status
                        ).toLowerCase()
                      : "";

                  const statusDisplay = getStatusDisplay(
                    status,
                    adminVerificationStatus,
                    latestTransaction?.payment_cancelled_reason || null,
                    latestTransaction?.payment_initiated_at || null
                  );

                  const isCompleted =
                    statusDisplay.text === "completed" ||
                    statusDisplay.pillKey === "released" ||
                    status === "released" ||
                    adminVerificationStatus === "released";

                  return isCompleted && latestTransaction?.id ? (
                    <Link
                      to={`/buyer/transactions/${latestTransaction.id}/invoice`}
                      className="mt-8 w-full border border-[#2A8BF7] px-4 py-3 rounded-[8px] text-[14px] text-[#2A8BF7] font-[600] flex items-center justify-center gap-2 bg-white hover:bg-blue-50 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="22"
                        viewBox="0 0 18 22"
                        fill="none"
                      >
                        <path
                          d="M11 1.26953V5.40007C11 5.96012 11 6.24015 11.109 6.45406C11.2049 6.64222 11.3578 6.7952 11.546 6.89108C11.7599 7.00007 12.0399 7.00007 12.6 7.00007H16.7305M11 16H5M13 12H5M17 8.98822V16.2C17 17.8802 17 18.7202 16.673 19.362C16.3854 19.9265 15.9265 20.3854 15.362 20.673C14.7202 21 13.8802 21 12.2 21H5.8C4.11984 21 3.27976 21 2.63803 20.673C2.07354 20.3854 1.6146 19.9265 1.32698 19.362C1 18.7202 1 17.8802 1 16.2V5.8C1 4.11984 1 3.27976 1.32698 2.63803C1.6146 2.07354 2.07354 1.6146 2.63803 1.32698C3.27976 1 4.11984 1 5.8 1H9.01178C9.74555 1 10.1124 1 10.4577 1.08289C10.7638 1.15638 11.0564 1.27759 11.3249 1.44208C11.6276 1.6276 11.887 1.88703 12.4059 2.40589L15.5941 5.59411C16.113 6.11297 16.3724 6.3724 16.5579 6.67515C16.7224 6.94356 16.8436 7.2362 16.9171 7.5423C17 7.88757 17 8.25445 17 8.98822Z"
                          stroke="#2A8BF7"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      See receipt
                    </Link>
                  ) : null;
                })()}
                {(() => {
                  const acceptedOffer = item?.acceptedOffer;
                  const transactions = item?.item?.transactions || [];
                  const latestTransaction =
                    transactions.length > 0 ? transactions[0] : null;
                  const hasAuthentication =
                    acceptedOffer?.is_delivered_door === true ||
                    latestTransaction?.is_delivered_door === true ||
                    item?.transaction?.is_delivered_door === true;
                  const authenticationFee =
                    hasAuthentication && deliveryFee ? Number(deliveryFee) : 0;

                  return hasAuthentication ? (
                    <div className="mt-8">
                      <h4 className="text-[20px] md:text-[24px] font-[600] text-[#1E1E1E] mb-4">
                        Additionnal services
                      </h4>
                      <div className="mb-4 rounded-[8px] bg-white">
                        <p className="text-[14px] text-[#1E1E1E] opacity-50">
                          Amount of the additional services will be deducted
                          from the amount held in escrow.
                        </p>
                      </div>
                      <div className="bg-[#E5F2FF] border border-[#00000026] rounded-[8px] p-2 px-4 flex items-center gap-3">
                        <div className="w-5 h-5 rounded border-2 bg-esblue border-esblue flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h5 className="text-[16px] font-[600] text-[#1E1E1E] mb-1">
                            ProtectPay product authentification
                          </h5>
                          <p className="text-[16px] text-[#000025] opacity-50">
                            Professional authentication service with certificate
                          </p>
                        </div>
                        <div className="text-[#575757] border border-[#00000026]  px-[10px] py-[4px] text-[14px] font-[600] rounded-[50px] ">
                          + {authenticationFee.toFixed(2)}€
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>

              {/* Show cancelled message if item is cancelled */}
              {item?.item && getUiStatus(item.item) === "cancelled" && (
                <div className="mt-8 p-4 bg-red-500 border border-gray-300 rounded-[8px]">
                  <p className="text-[16px] font-[600] text-white mb-2">
                    Cancelled
                  </p>
                  <p className="text-[14px] text-white">
                    {item?.transaction?.payment_cancelled_reason ===
                    "item_not_authentication"
                      ? "This item has been cancelled due to authentication check failure."
                      : "This item has been cancelled and is no longer available."}
                  </p>
                </div>
              )}

              {/* Offer History - Only shows current buyer's offers */}
              {offerHistory.length > 0 && (
                <div className="mt-6 h-[300px] overflow-y-auto bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                  <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik mb-4">
                    Your Offer History
                  </h3>
                  <div className="space-y-4">
                    {offerHistory.map((request) => {
                      const isCurrentUserOffer = request.buyer_id === user?.id;
                      if (!isCurrentUserOffer) return null;

                      const offerAmount = Number(request.offer_amount) || 0;
                      const isAccepted = request.status === "accepted";
                      const isRejected = request.status === "rejected";

                      return (
                        <div
                          key={request.id}
                          className="border border-[#E8F2FF] rounded-[8px] p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[12px] font-[600] px-2.5 py-1 rounded-[4px] ${
                                  isAccepted
                                    ? "bg-[#B9FFCC] text-[#16653F]"
                                    : isRejected
                                    ? "bg-[#FFD9D9] text-[#FF6E6E]"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {isAccepted
                                  ? "Accepted"
                                  : isRejected
                                  ? "Rejected"
                                  : "Pending"}
                              </span>
                              <span className="text-[12px] text-[#1E1E1E] opacity-50">
                                {new Date(
                                  request.created_at
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-[14px]">
                            <span className="text-[16px] text-[#1E1E1E] font-[600]">
                              Item price
                            </span>
                            <span className="text-[18px] text-[#000025] font-[700]">
                              {offerAmount.toFixed(2)} €
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* )} */}
              {/* <button className="mt-8 flex-1 md:flex-none text-[#16653F] w-full font-[600] text-[14px] py-2.5 rounded-[8px] inline-flex items-center justify-center gap-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                >
                  <path
                    d="M20.5 6L9.5 17L4.5 12"
                    stroke="#16653F"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Money received !</span>
              </button>
              <button className="mt-8 flex-1 md:flex-none border border-[#1E1E1E] border-opacity-50 text-[#16653F] w-full font-[600] text-[14px] py-2.5 rounded-[8px] inline-flex items-center justify-center gap-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                  className="opacity-50"
                >
                  <path
                    d="M4.5 7.8C4.5 6.11984 4.5 5.27976 4.82698 4.63803C5.1146 4.07354 5.57354 3.6146 6.13803 3.32698C6.77976 3 7.61984 3 9.3 3H15.7C17.3802 3 18.2202 3 18.862 3.32698C19.4265 3.6146 19.8854 4.07354 20.173 4.63803C20.5 5.27976 20.5 6.11984 20.5 7.8V21L17.75 19L15.25 21L12.5 19L9.75 21L7.25 19L4.5 21V7.8Z"
                    stroke="#1E1E1E"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>See receipt</span>
              </button> */}
            </div>

            {/* Seller information */}
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik mb-8">
                Seller information
              </h3>
              <div className="flex items-center gap-4">
                <img
                  src={
                    process.env.REACT_APP_IMAGE_URL +
                    item?.item?.seller?.profile_image_url
                  }
                  alt="avatar"
                  className="w-[65px] h-[65px] rounded-full"
                />
                <div>
                  <div className="text-[18px] opacity-75 font-[400] text-[#1E1E1E]">
                    {item?.item?.seller?.firstName +
                      " " +
                      item?.item?.seller?.lastName}
                  </div>
                  {/* <div className="text-[12px] opacity-50 text-[#1E1E1E]">
                    Luxury Assets Ltd.
                  </div> */}
                  <div className="text-[12px] opacity-50 text-[#1E1E1E]">
                    Joined August 2025
                  </div>
                </div>
                <span className="bg-[#EAF8EE] flex justify-center gap-1 items-center text-[#16653F] text-[14px] font-[600] px-[10px] py-1 rounded-full">
                  <img
                    src={VerifiedIcon}
                    alt={`thumb-verified`}
                    className="w-[12px] h-[12px] object-cover"
                  />
                  <span>Verified</span>
                </span>
              </div>
              <div className="mt-8 text-[14px]">
                <div className="flex items-center gap-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <g opacity="0.5">
                      <path
                        d="M21.5 18L14.8571 12M9.14286 12L2.50003 18M2 7L10.1649 12.7154C10.8261 13.1783 11.1567 13.4097 11.5163 13.4993C11.8339 13.5785 12.1661 13.5785 12.4837 13.4993C12.8433 13.4097 13.1739 13.1783 13.8351 12.7154L22 7M6.8 20H17.2C18.8802 20 19.7202 20 20.362 19.673C20.9265 19.3854 21.3854 18.9265 21.673 18.362C22 17.7202 22 16.8802 22 15.2V8.8C22 7.11984 22 6.27976 21.673 5.63803C21.3854 5.07354 20.9265 4.6146 20.362 4.32698C19.7202 4 18.8802 4 17.2 4H6.8C5.11984 4 4.27976 4 3.63803 4.32698C3.07354 4.6146 2.6146 5.07354 2.32698 5.63803C2 6.27976 2 7.11984 2 8.8V15.2C2 16.8802 2 17.7202 2.32698 18.362C2.6146 18.9265 3.07354 19.3854 3.63803 19.673C4.27976 20 5.11984 20 6.8 20Z"
                        stroke="#1E1E1E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                  </svg>
                  <span className="text-[16px] font-[400] text-[#000025]">
                    {item?.item?.seller?.email}
                  </span>
                </div>
                <div className="flex mt-4 items-center gap-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <g opacity="0.5">
                      <path
                        d="M8.38028 8.85335C9.07627 10.303 10.0251 11.6616 11.2266 12.8632C12.4282 14.0648 13.7869 15.0136 15.2365 15.7096C15.3612 15.7694 15.4235 15.7994 15.5024 15.8224C15.7828 15.9041 16.127 15.8454 16.3644 15.6754C16.4313 15.6275 16.4884 15.5704 16.6027 15.4561C16.9523 15.1064 17.1271 14.9316 17.3029 14.8174C17.9658 14.3864 18.8204 14.3864 19.4833 14.8174C19.6591 14.9316 19.8339 15.1064 20.1835 15.4561L20.3783 15.6509C20.9098 16.1824 21.1755 16.4481 21.3198 16.7335C21.6069 17.301 21.6069 17.9713 21.3198 18.5389C21.1755 18.8242 20.9098 19.09 20.3783 19.6214L20.2207 19.779C19.6911 20.3087 19.4263 20.5735 19.0662 20.7757C18.6667 21.0001 18.0462 21.1615 17.588 21.1601C17.1751 21.1589 16.8928 21.0788 16.3284 20.9186C13.295 20.0576 10.4326 18.4332 8.04466 16.0452C5.65668 13.6572 4.03221 10.7948 3.17124 7.76144C3.01103 7.19699 2.93092 6.91477 2.9297 6.50182C2.92833 6.0436 3.08969 5.42311 3.31411 5.0236C3.51636 4.66357 3.78117 4.39876 4.3108 3.86913L4.46843 3.7115C4.99987 3.18006 5.2656 2.91433 5.55098 2.76999C6.11854 2.48292 6.7888 2.48292 7.35636 2.76999C7.64174 2.91433 7.90747 3.18006 8.43891 3.7115L8.63378 3.90637C8.98338 4.25597 9.15819 4.43078 9.27247 4.60655C9.70347 5.26945 9.70347 6.12403 9.27247 6.78692C9.15819 6.96269 8.98338 7.1375 8.63378 7.4871C8.51947 7.60142 8.46231 7.65857 8.41447 7.72538C8.24446 7.96281 8.18576 8.30707 8.26748 8.58743C8.29048 8.66632 8.32041 8.72866 8.38028 8.85335Z"
                        stroke="#1E1E1E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                  </svg>
                  <span className="text-[16px] font-[400] text-[#000025]">
                    {item?.item?.seller?.phone
                      ? item?.item?.seller?.phone
                      : "N/A"}
                  </span>
                </div>
              </div>
              <div className="mt-8 flex justify-between flex-wrap text-center">
                <div className="text-left">
                  <div className="text-[12px] text-[#1E1E1E] font-[500] opacity-50">
                    Total sales
                  </div>
                  <div className="text-page-subtitle-d font-[700]">
                    {sellerStats.totalSales ?? 0}
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-[12px] text-[#1E1E1E] font-[500] opacity-50">
                    Total sold
                  </div>
                  <div className="text-page-subtitle-d font-[700]">
                    {sellerStats.totalSoldValue ?? 0} €
                  </div>
                </div>
              </div>
              <button
                onClick={handleContactSeller}
                className="mt-8 w-full bg-eslightBlue text-esblue py-3 rounded-[8px] text-[14px] font-[600]"
              >
                Contact seller
              </button>
            </div>

            {/* Security and protection */}
            <div className="bg-white border  border-[#DCEDFF] rounded-[12px] p-5">
              <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik mb-4">
                Security and protection
              </h3>
              <ul className="space-y-4 text-[14px]">
                <li className="flex items-center gap-3">
                  <span className="">
                    <img
                      src={VerifiedIconGray}
                      alt={`thumb-verified`}
                      className="object-contain"
                    />
                  </span>
                  <div className="space-y-1">
                    <p className="text-[#000025] font-[600] text-[16px]">
                      Escrow protection
                    </p>
                    <p className="text-[#000025] font-[400] opacity-50 text-[16px]">
                      Payment held securely until item received
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {isOfferOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setIsOfferOpen(false)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-[12px] p-5 md:p-8 max-w-[625px] w-full mx-4">
            {/* Header */}
            <h3 className="text-page-title md:text-page-title-d font-rubik text-gray-[#1E1E1E] mb-4">
              Send offer
            </h3>

            {/* Content */}
            <form onSubmit={handleSubmitOffer}>
              <div className="mb-8">
                <label
                  className="block text-[14px] font-[500] text-[#1E1E1E] mb-2"
                  htmlFor="offerPrice"
                >
                  Offer price
                </label>
                <input
                  id="offerPrice"
                  type="number"
                  step="0.01"
                  placeholder="Enter offer price"
                  value={offerPrice}
                  onChange={(e) => setOfferPrice(e.target.value)}
                  className="w-full border border-[#E8F2FF] rounded-[8px] px-3 py-2 outline-none focus:ring-2 focus:ring-esblue"
                />
                {offerError && (
                  <div className="text-red-500 text-[13px] mt-2">
                    {offerError}
                  </div>
                )}
                {offerPrice &&
                  !isNaN(Number(offerPrice)) &&
                  Number(offerPrice) > 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between text-[14px] text-[#1E1E1E]">
                        <span className="opacity-75">
                          Service fee ({item?.pricing?.buyerFeePercentage}%)
                        </span>
                        <span className="font-[500]">
                          {(
                            (Number(offerPrice) *
                              item?.pricing?.buyerFeePercentage) /
                            100
                          ).toFixed(2)}{" "}
                          €
                        </span>
                      </div>
                      {item?.item?.delivered_to_door &&
                        includeDeliveryFee &&
                        deliveryFee && (
                          <div className="flex justify-between text-[14px] text-[#1E1E1E]">
                            <span className="opacity-75">Delivery fee</span>
                            <span className="font-[500]">
                              {deliveryFee.toFixed(2)} €
                            </span>
                          </div>
                        )}
                      <div className="flex justify-between text-[16px] font-[600] text-[#1E1E1E] border-t border-[#E8F2FF] pt-2">
                        <span>Total</span>
                        <span>
                          {(
                            Number(offerPrice) +
                            (Number(offerPrice) *
                              item?.pricing?.buyerFeePercentage) /
                              100 +
                            (item?.item?.delivered_to_door &&
                            includeDeliveryFee &&
                            deliveryFee
                              ? deliveryFee
                              : 0)
                          ).toFixed(2)}{" "}
                          €
                        </span>
                      </div>
                    </div>
                  )}
              </div>

              {/* Delivery Fee Checkbox - Only show if delivered_to_door is true */}
              {item?.item?.delivered_to_door && (
                <div className="mb-8">
                  {deliveryFeeError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-600 text-[13px]">
                        {deliveryFeeError}
                      </p>
                    </div>
                  )}
                  <div
                    onClick={() => {
                      if (!deliveryFee && !deliveryFeeError) {
                        setDeliveryFeeError(
                          "Delivery fee information is not available. Please close and reopen the modal."
                        );
                        return;
                      }
                      if (deliveryFee) {
                        setIncludeDeliveryFee(!includeDeliveryFee);
                      }
                    }}
                    className={`flex flex-col md:flex-row md:items-center md:justify-between p-2 rounded-lg border-2 cursor-pointer transition-all ${
                      includeDeliveryFee
                        ? "bg-green-50 border-green-500"
                        : deliveryFeeError || !deliveryFee
                        ? "bg-gray-50 border-gray-300 opacity-60"
                        : "bg-white border-gray-200 hover:border-esblue"
                    } ${
                      !deliveryFee && !deliveryFeeError
                        ? "cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <div className="flex-1 flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          includeDeliveryFee
                            ? "bg-green-500 border-green-500"
                            : "border-gray-300"
                        }`}
                      >
                        {includeDeliveryFee && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className="text-[12px] text-gray-500 mb-0">
                          Authenticate and get it delivered to your door
                        </p>
                        {deliveryFee ? (
                          <p
                            className={`text-[18px] font-[600] ${
                              includeDeliveryFee
                                ? "text-green-600"
                                : "text-[#1E1E1E]"
                            }`}
                          >
                            {deliveryFee.toLocaleString()}€
                          </p>
                        ) : (
                          <p className="text-[18px] font-[600] text-red-600">
                            Not available
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 md:mt-0 md:ml-4">
                      {includeDeliveryFee ? (
                        <span className="text-green-600 font-[600] text-[14px]">
                          Added
                        </span>
                      ) : (
                        <span className="text-esblue font-[600] text-[14px] flex items-center gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          Add
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col md:flex-row gap-4 justify-end">
                <button
                  type="button"
                  onClick={() => setIsOfferOpen(false)}
                  className="px-4 py-3 order-2 md:order-1 text-esblue min-w-[147px] rounded-[8px] bg-[#F5F8FF]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingOffer}
                  className="px-4 py-3 order-1 md:order-2 bg-blue-gradient min-w-[147px] text-white text-nav-d rounded-[8px] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sendingOffer ? "Sending..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <Toast
          message={toastMessage}
          type="success"
          onClose={() => setToastMessage("")}
        />
      )}

      {/* Confirmation Modal */}
      {(() => {
        const transactions = item?.item?.transactions || [];
        const latestTransaction =
          transactions.length > 0 ? transactions[0] : null;
        return (
          <ConfirmTransactionModal
            isOpen={modalOpen}
            onClose={handleCloseModal}
            onConfirm={handleConfirmTransaction}
            transactionId={latestTransaction?.id}
            itemName={item?.item?.title}
            isLoading={confirmingId === latestTransaction?.id}
            errorText={modalError}
          />
        );
      })()}
    </LayoutBuyerSeller>
  );
};

export default ListingDetails;
